import React, { useEffect, useState } from "react";

import {
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  Link,
  Typography,
  makeStyles,
} from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";

import { carrierIdSchemeLabel } from "../../../utils/carrierIdentity";
import {
  FMCSA_SCHEMES,
  describeLookup,
  lookupCarrierIdentifier,
} from "../../../utils/carrierLookup";

/**
 * What FMCSA's register holds about the carrier being edited, beside the form.
 *
 * The form itself checks a number as it is typed — is this real, and whose is
 * it — and says no more than that, because a row being typed is not the place
 * for a carrier's whole file. Once the carrier is saved, the rest of the record
 * is what a yard actually wants before it hands over a load: whether the carrier
 * may operate today, whether it has been placed out of service, the address and
 * phone the register holds against the name in the form.
 *
 * Read-only, and never written back. The register's name and address are the
 * legal entity's, which is regularly not what the yard's paperwork says — the
 * form offers the legal name where a number is entered, and that is the one
 * place a person chooses to take it.
 *
 * Nothing here can stop the form working: the lookup already resolves every
 * failure into a state rather than an error, and a carrier with no US number is
 * the ordinary case for a yard on the Canadian side.
 */

const useStyles = makeStyles((theme) => ({
  root: {
    [theme.breakpoints.up("sm")]: {
      width: "20em",
      minWidth: "20em",
      marginLeft: theme.spacing(1),
      marginRight: theme.spacing(0.5),
      overflow: "initial",
    },
    // Hidden rather than stacked under the form, as the other asides are: it is
    // a second opinion on the record, and on a phone the record itself is
    // already more than one screen.
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
  field: {
    marginBottom: theme.spacing(1),
  },
}));

/**
 * The number to ask the register about.
 *
 * USDOT first, then the docket numbers, because a USDOT number is the register's
 * own primary key and answers with exactly one carrier — a docket can be held by
 * more than one, which is an answer the register cannot resolve and neither can
 * this panel.
 */
export const fmcsaIdentifierOf = (record) => {
  const identifiers = Array.isArray(record?.identifiers)
    ? record.identifiers
    : [];
  for (const scheme of FMCSA_SCHEMES) {
    const match = identifiers.find(
      (row) =>
        String(row?.scheme ?? "")
          .trim()
          .toUpperCase() === scheme && row?.value,
    );
    if (match) return { scheme, value: match.value };
  }
  return null;
};

/**
 * Ask once per number, and forget the answer if the number changes under us —
 * saving the form replaces the record, and the panel must not go on describing
 * the carrier that used to hold that row.
 */
const useFmcsaCarrier = (identifier) => {
  const [state, setState] = useState({ checking: false, result: null });
  const scheme = identifier?.scheme;
  const value = identifier?.value;

  useEffect(() => {
    if (!scheme || !value) {
      setState({ checking: false, result: null });
      return undefined;
    }

    let live = true;
    setState({ checking: true, result: null });
    // The lookup is cached per number for the life of the page, so reopening the
    // same carrier costs nothing and this stays a plain effect.
    lookupCarrierIdentifier({ scheme, value }).then((result) => {
      if (live) setState({ checking: false, result });
    });

    return () => {
      live = false;
    };
  }, [scheme, value]);

  return state;
};

// The register's public snapshot for this carrier — the page a yard would have
// gone to by hand, and the place to see the parts not summarised here (safety
// rating, inspection history, insurance).
const saferUrl = (dotNumber) =>
  `https://safer.fmcsa.dot.gov/query.asp?searchtype=ANY&query_type=queryCarrierSnapshot&query_param=USDOT&query_string=${encodeURIComponent(
    dotNumber,
  )}`;

const Field = ({ label, children }) => {
  const classes = useStyles();
  if (!children) return null;
  return (
    <div className={classes.field}>
      <Typography variant="overline" color="textSecondary" component="div">
        {label}
      </Typography>
      {/* component="div": a registered address is rendered as one div per
          line, and Typography's default <p> may not hold them. */}
      <Typography variant="body2" component="div">
        {children}
      </Typography>
    </div>
  );
};

const addressLines = (address) => {
  if (!address) return null;
  const cityLine = [address.city, address.state].filter(Boolean).join(", ");
  const lines = [
    address.street,
    [cityLine, address.zip].filter(Boolean).join(" "),
    // The register holds a country on every carrier, US included, and it is the
    // line that matters most on the ones a yard is least sure about.
    address.country,
  ].filter((line) => line && line.trim());
  return lines.length ? lines : null;
};

const CarrierDetails = ({ carrier }) => (
  <>
    <Field label="Registered name">{carrier.legalName}</Field>
    {carrier.dbaName && carrier.dbaName !== carrier.legalName && (
      <Field label="Doing business as">{carrier.dbaName}</Field>
    )}
    <Field label="USDOT number">{carrier.dotNumber}</Field>
    <Field label="Operation">{carrier.operationDescription}</Field>
    <Field label="Allowed to operate">
      {carrier.allowedToOperate === true
        ? "Yes"
        : carrier.allowedToOperate === false
          ? "Not per FMCSA"
          : "Could not determine"}
    </Field>
    {carrier.outOfService && (
      <Field label="Out of service">
        {carrier.outOfServiceDate || "Yes, date not given"}
      </Field>
    )}
    <Field label="Registered phone">{carrier.phone}</Field>
    {addressLines(carrier.address) && (
      <Field label="Registered address">
        {addressLines(carrier.address).map((line) => (
          <div key={line}>{line}</div>
        ))}
      </Field>
    )}
    {carrier.dotNumber && (
      <Typography variant="caption">
        <Link
          href={saferUrl(carrier.dotNumber)}
          target="_blank"
          rel="noopener noreferrer"
        >
          Full snapshot on SAFER
        </Link>
      </Typography>
    )}
  </>
);

/**
 * @param {object} props
 * @param {object} [props.record] - the carrier being edited; react-admin's Edit
 *   passes it in, and it is absent for the first paint while the record loads
 */
export const CarrierFmcsaAside = ({ record }) => {
  const classes = useStyles();
  const identifier = fmcsaIdentifierOf(record);
  const { checking, result } = useFmcsaCarrier(identifier);
  const verdict = describeLookup(result);

  if (!record) return null;

  return (
    <Card className={classes.root}>
      <CardContent>
        <Typography variant="subtitle2" gutterBottom>
          FMCSA register
        </Typography>

        {!identifier && (
          <Typography variant="body2" color="textSecondary">
            No USDOT, MC or MX number on this carrier, so there is nothing for
            FMCSA to answer about. Add one under ID Numbers.
          </Typography>
        )}

        {identifier && (
          <>
            <Typography
              variant="caption"
              color="textSecondary"
              component="div"
              gutterBottom
            >
              {`Looked up by ${carrierIdSchemeLabel(identifier.scheme)} ${
                identifier.value
              }`}
            </Typography>

            {checking && (
              <Typography variant="caption" color="textSecondary">
                <CircularProgress size={10} thickness={6} /> Checking FMCSA…
              </Typography>
            )}

            {/* The same one-line verdict the form shows where the number is
                typed, so the two places never disagree about a carrier — but
                only when it says something the fields below do not. A clean
                carrier's verdict is its registered name and trading name, which
                are the first two rows of the panel; repeating them as an alert
                spends the one attention-getting element here on the answer
                nobody needs to act on. Every other severity names something no
                field holds: out of service, barred, a lapsed registration, a
                docket held twice, a register that did not answer. */}
            {!checking && verdict && verdict.severity !== "success" && (
              <Alert severity={verdict.severity} icon={false}>
                <Typography variant="caption" component="span">
                  {verdict.text}
                </Typography>
              </Alert>
            )}

            {/* Only for the single-carrier answer. An ambiguous docket has
                already been named in the verdict above, and picking one of its
                candidates to describe here is exactly the guess that puts a
                yard's paperwork under the wrong company. */}
            {!checking && result?.found && !result.ambiguous && (
              <>
                <Divider style={{ margin: "12px 0" }} />
                <Grid container direction="column">
                  <CarrierDetails carrier={result.carrier || {}} />
                </Grid>
              </>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};
